import type { ComponentChildren } from "preact";
import { cn } from "./cn";

export type SegmentedOption<T extends string> = {
  value: T;
  label: ComponentChildren;
  disabled?: boolean;
};

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = "md",
  class: extraClass,
}: {
  value: T;
  onChange: (next: T) => void;
  options: SegmentedOption<T>[];
  ariaLabel?: string;
  size?: "sm" | "md";
  class?: string;
}) {
  const padding = size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs";
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      class={cn(
        "inline-flex items-center rounded-md border border-border bg-bg p-0.5 font-medium text-fg-subtle",
        extraClass,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => !active && !opt.disabled && onChange(opt.value)}
            disabled={opt.disabled}
            aria-pressed={active}
            class={cn(
              "whitespace-nowrap rounded-[4px] transition",
              padding,
              active
                ? "bg-accent text-accent-fg shadow-xs"
                : "text-fg-subtle hover:text-fg",
              opt.disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
